import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  CircularProgress, Alert, Avatar, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { listStartupUpdates, createStartupUpdate, editStartupUpdate, deleteStartupUpdate } from '../services/api';

const StartupUpdates = () => {
  const { startupId } = useParams();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUpdates = useCallback(async () => {
    try {
      const res = await listStartupUpdates(startupId);
      setUpdates(res.data?.updates || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load updates');
    } finally { setLoading(false); }
  }, [startupId]);

  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await editStartupUpdate(editingId, { title, body });
      } else {
        await createStartupUpdate(startupId, { title, body });
      }
      setDialogOpen(false); setTitle(''); setBody(''); setEditingId(null);
      await fetchUpdates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save update');
    } finally { setSubmitting(false); }
  };

  const handleEdit = (update) => {
    setEditingId(update.id);
    setTitle(update.title || '');
    setBody(update.body || update.content || '');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try { await deleteStartupUpdate(id); await fetchUpdates(); } catch (_) { setError('Failed to delete'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>Startup Updates</Typography>
              <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Latest news and milestones</Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setEditingId(null); setTitle(''); setBody(''); setDialogOpen(true); }}
              sx={{ backgroundColor: 'white', color: '#052d50', '&:hover': { backgroundColor: '#e0e0e0' } }}>
              Post Update
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {updates.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <UpdateIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No updates yet</Typography>
          </Card>
        ) : (
          updates.map((u, i) => (
            <Card key={u.id || i} sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>{u.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#478289' }}>
                        {(u.author?.name || 'A')[0]}
                      </Avatar>
                      <Typography variant="caption" color="textSecondary">
                        {u.author?.name || 'Team'} · {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''}
                      </Typography>
                      {u.type && <Chip label={u.type} size="small" variant="outlined" />}
                    </Box>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleEdit(u)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(u.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {u.body || u.content}
                </Typography>
              </CardContent>
            </Card>
          ))
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Update' : 'Post Update'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Content" value={body} onChange={(e) => setBody(e.target.value)} multiline rows={6} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting || !title.trim()}
            sx={{ backgroundColor: '#478289' }}>
            {submitting ? <CircularProgress size={20} /> : editingId ? 'Save' : 'Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StartupUpdates;
